import React, { useState, useEffect, useRef } from 'react';
import toast from 'react-hot-toast';
import { ChevronDown, Check, X } from 'lucide-react';
import api from '../../../connector/api';

// Custom Reusable Dropdown Component
function CustomSelect({ options, value, onChange, placeholder, label }) {
    const [isOpen, setIsOpen] = useState(false);
    const dropdownRef = useRef(null);

    const selectedOption = options.find((opt) => String(opt.id) === String(value));

    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    return (
        <div className="space-y-1 relative" ref={dropdownRef}>
            <label className="text-xs font-medium text-zinc-700 block">{label}</label>
            <button
                type="button"
                onClick={() => setIsOpen(!isOpen)}
                className="w-full text-xs text-left text-zinc-900 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 flex items-center justify-between outline-none focus:border-blue-500 focus:bg-white transition-colors"
            >
                <span className={selectedOption ? "text-zinc-900" : "text-zinc-400"}>
                    {selectedOption ? selectedOption.name : placeholder}
                </span>
                <ChevronDown className={`w-3.5 h-3.5 text-zinc-400 transition-transform ${isOpen ? 'rotate-180' : ''}`} />
            </button>

            {isOpen && (
                <div className="absolute z-10 top-full left-0 right-0 mt-1 bg-white border border-zinc-200 rounded-lg shadow-lg max-h-48 overflow-y-auto py-1">
                    {options.length === 0 ? (
                        <div className="px-3 py-2 text-xs text-zinc-400 italic">No options available</div>
                    ) : (
                        options.map((opt) => (
                            <button
                                key={opt.id}
                                type="button"
                                onClick={() => {
                                    onChange(opt.id);
                                    setIsOpen(false);
                                }}
                                className="w-full text-left px-3 py-2 text-xs flex items-center justify-between hover:bg-zinc-50 transition-colors"
                            >
                                <span className={String(opt.id) === String(value) ? "font-semibold text-blue-600" : "text-zinc-700"}>
                                    {opt.name}
                                </span>
                                {String(opt.id) === String(value) && (
                                    <Check className="w-3.5 h-3.5 text-blue-600" />
                                )}
                            </button>
                        ))
                    )}
                </div>
            )}
        </div>
    );
}

export default function YearTermLinkModal({ link, schoolYears, terms, onClose, onSuccess }) {
    const [schoolYearId, setSchoolYearId] = useState('');
    const [termId, setTermId] = useState('');
    const [startDate, setStartDate] = useState('');
    const [endDate, setEndDate] = useState('');
    const [isCurrent, setIsCurrent] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    useEffect(() => {
        if (link) {
            setSchoolYearId(link.school_year?.id ?? link.school_year ?? '');
            setTermId(link.term?.id ?? link.term ?? '');
            setStartDate(link.start_date || '');
            setEndDate(link.end_date || '');
            setIsCurrent(!!link.is_current);
        } else {
            setSchoolYearId('');
            setTermId('');
            setStartDate('');
            setEndDate('');
            setIsCurrent(false);
        }
    }, [link]);

    const handleSubmit = async (e) => {
        e.preventDefault();

        if (!schoolYearId || !termId) {
            toast.error("Please select both a school year and term.");
            return;
        }

        setSubmitting(true);

        const payload = {
            school_year_id: Number(schoolYearId),
            term_id: Number(termId),
            start_date: startDate,
            end_date: endDate,
            is_current: isCurrent
        };

        try {
            if (link?.id) {
                await api.patch(`/year-terms/${link.id}/`, payload);
                toast.success("Scope connection updated.");
            } else {
                const createPayload = {
                    school_year: Number(schoolYearId),
                    term: Number(termId),
                    start_date: startDate,
                    end_date: endDate,
                    is_current: isCurrent
                };
                await api.post('/year-terms/set-year-term/', createPayload);
                toast.success("Scope connection mapped.");
            }
            onSuccess();
        } catch (err) {
            console.error("Failed to map scope:", err);
            toast.error("Failed to map connection scope.");
        } finally {
            setSubmitting(false);
        }
    };

    return (
        /* Explicit screen edge positioning prevents top gap margin leaks */
        <div className="fixed top-0 left-0 right-0 bottom-0 z-50 flex items-center justify-center p-4 bg-black/50 backdrop-blur-xs">
            <div className="bg-white border border-zinc-200 rounded-lg max-w-md w-full shadow-xl p-6 space-y-4">
                <div className="flex items-center justify-between border-b border-zinc-100 pb-3">
                    <h3 className="text-sm font-bold text-zinc-900">
                        {link ? 'Edit Scope Connection' : 'Connect Scope'}
                    </h3>
                    <button 
                        onClick={onClose} 
                        className="p-1 text-zinc-400 hover:text-zinc-600 rounded-md transition-colors"
                    >
                        <X className="w-4 h-4" />
                    </button>
                </div>

                <form onSubmit={handleSubmit} className="space-y-4">
                    <CustomSelect
                        label="School Year"
                        placeholder="Select Year..."
                        options={schoolYears}
                        value={schoolYearId}
                        onChange={(val) => setSchoolYearId(val)}
                    />

                    <CustomSelect
                        label="Academic Term"
                        placeholder="Select Term..."
                        options={terms}
                        value={termId}
                        onChange={(val) => setTermId(val)}
                    />

                    <div className="grid grid-cols-2 gap-3">
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-zinc-700 block">Start Bound</label>
                            <input
                                type="date"
                                className="w-full text-xs text-zinc-900 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 outline-none focus:border-blue-500 focus:bg-white transition-colors"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                                required
                            />
                        </div>
                        <div className="space-y-1">
                            <label className="text-xs font-medium text-zinc-700 block">End Bound</label>
                            <input
                                type="date"
                                className="w-full text-xs text-zinc-900 bg-zinc-50 border border-zinc-200 rounded-lg px-3 py-2 outline-none focus:border-blue-500 focus:bg-white transition-colors"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                                required
                            />
                        </div>
                    </div>

                    <div className="flex items-center gap-2 pt-1">
                        <input
                            type="checkbox"
                            id="is_current"
                            checked={isCurrent}
                            onChange={(e) => setIsCurrent(e.target.checked)}
                            className="rounded text-blue-600 focus:ring-blue-500 h-4 w-4 border-zinc-300"
                        />
                        <label htmlFor="is_current" className="text-xs font-medium text-zinc-700 select-none cursor-pointer">
                            Mark as current active schedule term
                        </label>
                    </div>

                    <div className="flex justify-end gap-2 pt-2">
                        <button
                            type="button"
                            onClick={onClose}
                            className="px-3 py-1.5 text-xs font-medium text-zinc-600 bg-zinc-100 hover:bg-zinc-200 rounded-lg transition-colors"
                        >
                            Cancel
                        </button>
                        <button
                            type="submit"
                            disabled={submitting}
                            className="px-3 py-1.5 text-xs font-semibold text-white bg-blue-600 hover:bg-blue-700 rounded-lg transition-colors shadow-sm disabled:opacity-50"
                        >
                            {submitting ? "Saving..." : link ? "Update Link" : "Save Link"}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
}